"use client";

import { useTranslations } from "next-intl";
import { PulseAnimation, Stack, Text } from "@leabankab/component-library";
import { StepCard } from "@/components/layout/step-card";
import { type WaitingPhase } from "@/features/waiting/poll-schedule";

/**
 * Centered pulse spinner. Context-free so it can safely render inside a
 * route Suspense fallback even while a step transition is in flight.
 */
export const StepLoader = ({ padded = true }: { padded?: boolean }) => (
	<div
		className={`flex w-full flex-1 items-center justify-center ${padded ? "py-6" : ""}`}
	>
		<PulseAnimation />
	</div>
);

/**
 * Loader with the waiting copy, for routes that already have a card frame.
 * Inside the flow layout the progress header owns the `h1`, so pass `as="h2"`.
 */
export const StepLoaderBody = ({
	as = "h1",
	phase = "normal",
}: {
	as?: "h1" | "h2";
	phase?: WaitingPhase;
}) => {
	const t = useTranslations("common.loading");
	const copy = {
		normal: { title: t("title"), description: t("description") },
		slow: { title: t("slow.title"), description: t("slow.description") },
		verySlow: {
			title: t("verySlow.title"),
			description: t("verySlow.description"),
		},
		offline: {
			title: t("offline.title"),
			description: t("offline.description"),
		},
		unavailable: {
			title: t("unavailable.title"),
			description: t("unavailable.description"),
		},
	}[phase];
	const Heading = as;

	return (
		<Stack gap={1} className="py-6">
			<StepLoader padded={false} />
			<Stack aria-live="polite">
				<Text
					render={<Heading />}
					textStyle="heading-sm"
					color="text.base"
					align="center"
				>
					{copy.title}
				</Text>
				<Text color="text.subtle" align="center">
					{copy.description}
				</Text>
			</Stack>
		</Stack>
	);
};

/** Step loader wrapped in the shared step card frame. */
export const StepLoaderCard = () => (
	<StepCard>
		<StepLoaderBody />
	</StepCard>
);
